export { GeneralInsuranceTab } from './GeneralInsuranceTab.jsx';
export { MutualFundTab } from './MutualFundTab.jsx';

import React, { useState, useMemo, useEffect } from 'react';
import Table from './common/Table.jsx';
import Card from './common/Card.jsx';
import LoadingSpinner from './common/LoadingSpinner.jsx';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from 'recharts';
import { getMonthlyPolicyPremiums, getImageUrl, API_BASE_URL } from '../../model/api.js';
import { postersAPI } from '../../services/api.js';
import ImageCropper from './common/ImageCropper.jsx';
import {
  DollarIcon,
  UserGroupIcon,
  RefreshIcon,
  GridIcon,
  TableIcon,
  PlusCircleIcon,
  PolicyIcon,
  ChartBarIcon
} from './common/Icons.jsx';

// Dashboard Tab Component
const DashboardTab = ({ data }) => {

  if (!data) {
    return null;
  }

  // Year selector and monthly premium aggregation for the selected year
  const thisYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(thisYear.toString());
  const years = Array.from({ length: 6 }, (_, i) => (thisYear - i).toString());
  const [monthlyPolicyData, setMonthlyPolicyData] = useState([]);
  const [loadingPolicyData, setLoadingPolicyData] = useState(true);

  // Fetch monthly policy premiums when year changes
  useEffect(() => {
    const fetchMonthlyPolicyData = async () => {
      setLoadingPolicyData(true);
      try {
        const result = await getMonthlyPolicyPremiums(selectedYear);
        if (result && result.monthlyData) {
          setMonthlyPolicyData(result.monthlyData);
        } else {
          setMonthlyPolicyData([]);
        }
      } catch (error) {
        console.error('Failed to fetch monthly policy data:', error);
        setMonthlyPolicyData([]);
      } finally {
        setLoadingPolicyData(false);
      }
    };

    fetchMonthlyPolicyData();
  }, [selectedYear]);

  // current month helper
  const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM format
  const [currentYear, currentMonthNum] = currentMonth.split('-').map(Number);

  // Derived metrics
  const totalPayoutsCount = (data.payouts || []).length;

  const totalRevenue = useMemo(() => {
    // Use the totalRevenue from backend if available (calculated from policies)
    if (data.totalRevenue !== undefined) return data.totalRevenue;
    // Fallback to commission breakdown
    if (data.commissionBreakdown && data.commissionBreakdown.totalRevenue) return data.commissionBreakdown.totalRevenue;
    // Sum from payouts as final fallback
    return (data.payouts || []).reduce((sum, p) => {
      const v = Number(p.totalRevenue || p.revenue || p.total_revenue || 0) || 0;
      return sum + v;
    }, 0);
  }, [data.payouts, data.totalRevenue, data.commissionBreakdown]);

  const uniqueLeadersCount = useMemo(() => {
    const s = new Set();
    (data.payouts || []).forEach(p => {
      if (p.leaderName) s.add(p.leaderName);
      else if (p.leader_id && (p.leader_id._id || p.leader_id)) s.add(p.leader_id._id || p.leader_id);
    });
    return s.size;
  }, [data.payouts]);

  const avgMonthlyRevenueForSelectedYear = useMemo(() => {
    const total = monthlyPolicyData.reduce((s, m) => s + (m.premium || 0), 0);
    return total / 12;
  }, [monthlyPolicyData]);

  const ramaCommission = data.payouts?.find(p =>
    p.leaderName === 'Rama' &&
    p.month === currentMonth
  );

  const ragavanCommission = data.payouts?.find(p =>
    p.leaderName === 'ragavan' &&
    p.month === currentMonth
  );

  const superAdminCommission = data.payouts?.find(p =>
    p.leaderName === 'Super Admin' &&
    p.month === currentMonth
  );

  return (
    <div className="space-y-6">
      {/* Summary Cards (replaced old, less useful ones) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        <Card
          title="Total Revenue"
          value={`₹${(totalRevenue || 0).toLocaleString('en-IN')}`}
          icon={<DollarIcon />}
          footer={
            <span className="text-sm text-gray-500">Calculated from policy data</span>
          }
        />

        <Card
          title="Total Payouts"
          value={totalPayoutsCount}
          icon={<PolicyIcon />}
        />

        <Card
          title="Active Leaders"
          value={uniqueLeadersCount}
          icon={<UserGroupIcon />}
        />

        <Card
          title={`Avg Monthly (${selectedYear})`}
          value={`₹${Math.round(avgMonthlyRevenueForSelectedYear || 0).toLocaleString('en-IN')}`}
          icon={<ChartBarIcon />}
          footer={<span className="text-sm text-gray-500">Across Jan–Dec</span>}
        />
      </div>
      {/* Year selector + Monthly Revenue Chart */}
      <div className="bg-white p-4 rounded-lg shadow-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Year-wise Monthly Premiums</h3>
          <div className="flex items-center gap-3">
            <label htmlFor="year-select" className="text-sm text-gray-600">Year</label>
            <select
              id="year-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ width: '100%', height: 300 }}>
          {loadingPolicyData ? (
            <div className="flex items-center justify-center h-full">
              <LoadingSpinner />
            </div>
          ) : (
            <ResponsiveContainer>
              <BarChart data={monthlyPolicyData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => `₹${Number(value).toLocaleString('en-IN')}`} />
                <Bar dataKey="premium" fill="#4f46e5" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

    </div>
  );
};

// Payouts Tab Component
// Payouts Tab Component
const PayoutsTab = ({
  data,
  payoutColumns,
  payoutTableTitle,
  recalculating,
  fetchData,
  CommissionManager,
  onRecalculate
}) => {
  const filteredPayouts = data.payouts || [];

  return (
    <div className="space-y-8">
      <CommissionManager onRecalculate={onRecalculate} isRecalculating={recalculating} />
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">{payoutTableTitle}</h3>
          <button onClick={fetchData} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm flex items-center">
            <RefreshIcon className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
        <div className="relative">
          {recalculating && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <LoadingSpinner />
            </div>
          )}
          <Table columns={payoutColumns} data={filteredPayouts} />
        </div>
      </div>
    </div>
  );
};

// Paid Payouts Tab Component
const PaidPayoutsTab = ({
  paidPayouts,
  paidPayoutColumns,
  loading,
  selectedMonth,
  setSelectedMonth,
  selectedYear,
  setSelectedYear,
  fetchPaidPayouts
}) => {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-4">
          <div>
            <label htmlFor="paid-month-select" className="block text-sm font-medium text-gray-700 mb-1">Select Month</label>
            <select
              id="paid-month-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Months</option>
              {Array.from({ length: 12 }, (_, i) => {
                const month = (i + 1).toString().padStart(2, '0');
                const monthName = new Date(2000, i, 1).toLocaleString('default', { month: 'long' });
                return (
                  <option key={month} value={month}>
                    {monthName}
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <label htmlFor="paid-year-select" className="block text-sm font-medium text-gray-700 mb-1">Select Year</label>
            <select
              id="paid-year-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Years</option>
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">
            Showing {paidPayouts.length} paid payouts
          </span>

        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <Table
          title={`Paid Payouts${selectedMonth && selectedYear ? ` - ${new Date(selectedYear, parseInt(selectedMonth) - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' })}` : ''}`}
          columns={paidPayoutColumns}
          data={paidPayouts}
        />
      )}

      {paidPayouts.length === 0 && !loading && (
        <div className="text-center py-8">
          <p className="text-gray-500">No paid payouts found for the selected filters.</p>
        </div>
      )}
    </div>
  );
};

// Reports Tab Component
const ReportsTab = ({ data, performerColumns, onMonthChange }) => {
  const [selectedMonth, setSelectedMonth] = useState(() => {
    return new Date().toISOString().slice(0, 7); // YYYY-MM format
  });

  // Handle month change
  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setSelectedMonth(newMonth);
    if (onMonthChange) {
      onMonthChange(newMonth);
    }
  };

  // Enhanced columns with policy count
  const enhancedColumns = [
    {
      header: 'Rank',
      accessor: function (item, index) { return `#${index + 1}`; }
    },
    {
      header: 'Name',
      accessor: function (item) { return item.name; }
    },
    {
      header: 'Total Premium',
      accessor: function (item) { return `₹${Math.round(item.value).toLocaleString('en-IN')}`; }
    },
    {
      header: 'Policies',
      accessor: function (item) { return item.policyCount || 0; }
    }
  ];

  return (
    <div className="space-y-6">
      {/* Month selector */}
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-4">
          <div>
            <label htmlFor="month-select" className="block text-sm font-medium text-gray-700 mb-1">
              Select Month
            </label>
            <input
              id="month-select"
              type="month"
              value={selectedMonth}
              onChange={handleMonthChange}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div className="text-sm text-gray-600 mt-6">
            Showing rankings for {new Date(selectedMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}
          </div>
        </div>
      </div>

      {/* Rankings tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Table
          title="Top 10 Leaders"
          subtitle="Based on total policy premiums"
          columns={enhancedColumns}
          data={data.topLeaders || []}
        />
        <Table
          title="Top 10 Advisors"
          subtitle="Based on total policy premiums"
          columns={enhancedColumns}
          data={data.topAdvisors || []}
        />
      </div>
    </div>
  );
};



// Users Tab Component
const UsersTab = ({
  usersData,
  userSearchTerm,
  setUserSearchTerm,
  filteredUsers,
  userColumns,
  showUserForm,
  setShowUserForm,
  newUser,
  setNewUser,
  handleCreateUser,
  showEditForm,
  setShowEditForm,
  editUser,
  setEditUser,
  handleUpdateUser,
  handleEditUser,
  handleDeleteUser,
  getAvailableLeaders,
  uploadResults,
  setUploadResults,
  handleExcelUpload,
  showManualPolicyForm,
  setShowManualPolicyForm,
  newPolicy,
  setNewPolicy,
  handleManualPolicySubmit,
  creatingUser
}) => {
  const [posterCategories, setPosterCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Fetch poster categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const categories = await postersAPI.getCategories();
        setPosterCategories(categories || []);
      } catch (error) {
        console.error('Failed to fetch poster categories:', error);
        setPosterCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  const [cropperImage, setCropperImage] = useState(null);
  const [cropperTarget, setCropperTarget] = useState(null);

  const handleFileChange = (e, target) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setCropperImage(reader.result);
        setCropperTarget(target);
      };
      reader.readAsDataURL(file);
    }
    // Reset file input value so same file can be selected again if needed
    e.target.value = '';
  };

  const onCropComplete = (croppedBlob) => {
    // Generate a unique filename to avoid caching issues and help backend identified it's a new file
    const filename = `profile_photo_${Date.now()}.jpg`;
    const file = new File([croppedBlob], filename, { type: 'image/jpeg' });

    if (cropperTarget === 'new') {
      setNewUser({ ...newUser, profile_photo: file });
    } else {
      setEditUser({ ...editUser, profile_photo: file });
    }

    setCropperImage(null);
    setCropperTarget(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-4">
          <div>
            <label htmlFor="user-search-input" className="block text-sm font-medium text-gray-700 mb-1">Search Users</label>
            <input
              id="user-search-input"
              type="text"
              placeholder="Search by name, email, phone, role, or WealthPlus ID..."
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 w-80"
            />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">Showing {filteredUsers.length} of {usersData.length} users</span>
          <button
            onClick={() => {
              if (!showUserForm) {
                setNewUser({
                  name: '',
                  email: '',
                  phone: '',
                  role: 'advisor',
                  leader_id: '',
                  wealthplus_id: '',
                  pin: '',
                  categories: []
                });
              }
              setShowUserForm(!showUserForm);
            }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            {showUserForm ? 'Cancel' : 'Add New User'}
          </button>
        </div>
      </div>

      {/* User creation form */}
      {showUserForm && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h4 className="text-md font-semibold text-gray-800 mb-4">Create New User</h4>
          <form onSubmit={handleCreateUser} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <input
                type="tel"
                value={newUser.phone}
                onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Role</label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="advisor">Advisor</option>
                <option value="leader">Leader</option>
                <option value="health_plus_partner">Health Plus Partner</option>
              </select>
            </div>
            {newUser.role === 'advisor' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Direct Leader
                  <span className="text-xs text-gray-500 block">Select the leader who will directly oversee this advisor</span>
                </label>
                <select
                  value={newUser.leader_id || ''}
                  onChange={(e) => setNewUser({ ...newUser, leader_id: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Select a leader</option>
                  {getAvailableLeaders('advisor').map(leader => (
                    <option key={leader._id} value={leader._id}>
                      {leader.name} (WealthPlus: {leader.wealthplus_id || leader.tata_id})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {newUser.role === 'leader' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Upline Leader
                  <span className="text-xs text-gray-500 block">Select an existing leader who will oversee this new leader</span>
                </label>
                <select
                  value={newUser.leader_id || ''}
                  onChange={(e) => setNewUser({ ...newUser, leader_id: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  {getAvailableLeaders('leader').map(leader => (
                    <option key={leader._id} value={leader._id}>
                      {leader.name} (WealthPlus {leader.wealthplus_id || leader.tata_id})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {(newUser.role === 'advisor' || newUser.role === 'leader') && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  WealthPlus ID
                  <span className="text-xs text-gray-500 block">Unique identifier for this user in the WealthPlus system</span>
                </label>
                <input
                  type="text"
                  value={newUser.wealthplus_id}
                  onChange={(e) => setNewUser({ ...newUser, wealthplus_id: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Enter WealthPlus ID (e.g., WP001)"
                  required
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700">PIN</label>
              <input
                type="password"
                value={newUser.pin}
                onChange={(e) => setNewUser({ ...newUser, pin: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {newUser.role === 'advisor' ? 'User Designation' : 'Designation'}
              </label>
              <input
                type="text"
                value={newUser.designation_title || ''}
                onChange={(e) => setNewUser({ ...newUser, designation_title: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="e.g. Financial Consultant"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Profile Photo</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'new')}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              {newUser.profile_photo && (
                <div className="mt-2 flex items-center space-x-2">
                  <span className="text-xs text-green-600 font-medium">✓ Photo ready for upload</span>
                  <button
                    type="button"
                    onClick={() => setNewUser({ ...newUser, profile_photo: null })}
                    className="text-xs text-red-500 hover:text-red-700 underline"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Categories</label>
              {newUser.categories && newUser.categories.length > 0 && (
                <div className="mb-2">
                  <div className="flex flex-wrap gap-2">
                    {newUser.categories.map(catId => {
                      const category = posterCategories.find(c => c._id === catId);
                      return category ? (
                        <span key={catId} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          {category.name}
                          <button
                            type="button"
                            onClick={() => {
                              const index = newUser.categories.indexOf(catId);
                              if (index > -1) {
                                const newCategories = [...newUser.categories];
                                newCategories.splice(index, 1);
                                setNewUser({ ...newUser, categories: newCategories });
                              }
                            }}
                            className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-indigo-400 hover:bg-indigo-200 hover:text-indigo-500"
                          >
                            ×
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              <select
                multiple
                value={newUser.categories || []}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, option => option.value);
                  setNewUser({ ...newUser, categories: selected });
                }}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {posterCategories.map(category => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple categories</p>
            </div>
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={creatingUser}
                className={`px-4 py-2 text-white rounded-md ${creatingUser ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
              >
                {creatingUser ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </span>
                ) : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      )}

      {cropperImage && (
        <ImageCropper
          image={cropperImage}
          onCropComplete={onCropComplete}
          onCancel={() => {
            setCropperImage(null);
            setCropperTarget(null);
          }}
        />
      )}


      {/* User edit form */}
      {showEditForm && (
        <div className="bg-white p-6 rounded-lg shadow-md mt-4">
          <h4 className="text-md font-semibold text-gray-800 mb-4">Edit User</h4>
          <form onSubmit={handleUpdateUser} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                value={editUser.name}
                onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={editUser.email}
                onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <input
                type="tel"
                value={editUser.phone}
                onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            {editUser.role === 'advisor' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Leader</label>
                <select
                  value={editUser.leader_id || ''}
                  onChange={(e) => setEditUser({ ...editUser, leader_id: e.target.value, leader_phone: '' })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select a leader</option>
                  {getAvailableLeaders('advisor', editUser.id).map(leader => (
                    <option key={leader._id} value={leader._id}>
                      {leader.name} ({leader.wealthplus_id || leader.tata_id})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {editUser.role === 'leader' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Upline Leader (Optional)</label>
                <select
                  value={editUser.leader_id || ''}
                  onChange={(e) => setEditUser({ ...editUser, leader_id: e.target.value, leader_phone: '' })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">No upline leader (Top Level)</option>
                  {getAvailableLeaders('leader', editUser.id).map(leader => (
                    <option key={leader._id} value={leader._id}>
                      {leader.name} ({leader.wealthplus_id || leader.tata_id})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {(editUser.role === 'advisor' || editUser.role === 'leader') && (
              <div>
                <label className="block text-sm font-medium text-gray-700">WealthPlus ID <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={editUser.wealthplus_id}
                  onChange={(e) => setEditUser({ ...editUser, wealthplus_id: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700">Role</label>
              <select
                value={editUser.role}
                onChange={(e) => {
                  const newRole = e.target.value;
                  const updatedUser = { ...editUser, role: newRole };
                  // Clear fields that are not relevant for the new role
                  if (newRole === 'super_admin') {
                    updatedUser.leader_id = '';
                    updatedUser.wealthplus_id = '';
                  } else if (newRole === 'leader') {
                    // Keep leader_id for upline leader, clear wealthplus_id if not set
                    updatedUser.wealthplus_id = updatedUser.wealthplus_id || '';
                  } else if (newRole === 'advisor') {
                    // Keep leader_id for direct leader, clear wealthplus_id if not set
                    updatedUser.wealthplus_id = updatedUser.wealthplus_id || '';
                  } else if (newRole === 'health_plus_partner') {
                    updatedUser.wealthplus_id = '';
                    updatedUser.leader_id = '';
                  }
                  setEditUser(updatedUser);
                }}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="advisor">Advisor</option>
                <option value="leader">Leader</option>
                <option value="health_plus_partner">Health Plus Partner</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {editUser.role === 'advisor' ? 'User Designation' : 'Designation'}
              </label>
              <input
                type="text"
                value={editUser.designation_title || ''}
                onChange={(e) => setEditUser({ ...editUser, designation_title: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="e.g. Financial Consultant"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Update Profile Photo</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'edit')}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              {editUser.profile_photo && (
                <div className="mt-2 flex items-center space-x-2">
                  <span className="text-xs text-green-600 font-medium">✓ New photo cropped and ready</span>
                  <button
                    type="button"
                    onClick={() => setEditUser({ ...editUser, profile_photo: null })}
                    className="text-xs text-red-500 hover:text-red-700 underline"
                  >
                    Cancel update
                  </button>
                </div>
              )}
              {editUser.profile_photo_url && !editUser.profile_photo && (
                <p className="text-xs text-gray-500 mt-1">Current: <a href={`${API_BASE_URL}/users/${editUser._id}/photo`} target="_blank" rel="noreferrer" className="text-indigo-600 underline">View Current Photo</a></p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Categories</label>
              {editUser.categories && editUser.categories.length > 0 && (
                <div className="mb-2">
                  <div className="flex flex-wrap gap-2">
                    {editUser.categories.map(catId => {
                      const category = posterCategories.find(c => c._id === catId);
                      return category ? (
                        <span key={catId} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          {category.name}
                          <button
                            type="button"
                            onClick={() => {
                              const index = editUser.categories.indexOf(catId);
                              if (index > -1) {
                                const newCategories = [...editUser.categories];
                                newCategories.splice(index, 1);
                                setEditUser({ ...editUser, categories: newCategories });
                              }
                            }}
                            className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-indigo-400 hover:bg-indigo-200 hover:text-indigo-500"
                          >
                            ×
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              <select
                multiple
                value={editUser.categories || []}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, option => option.value);
                  setEditUser({ ...editUser, categories: selected });
                }}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {posterCategories.map(category => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple categories</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Change Password (Optional)</label>
              <input
                type="password"
                value={editUser.newPassword || ''}
                onChange={(e) => setEditUser({ ...editUser, newPassword: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Leave empty to keep current password"
              />
              <p className="text-xs text-gray-500 mt-1">Only enter a new password if you want to change it</p>
            </div>
            <div className="md:col-span-2 flex space-x-2">
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Update User
              </button>
              <button
                type="button"
                onClick={() => setShowEditForm(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Upload results */}
      {uploadResults && (
        <div className="bg-white p-6 rounded-lg shadow-md mt-4">
          <h4 className="text-md font-semibold text-gray-800 mb-4">Upload Results</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{uploadResults.inserted || 0}</div>
              <div className="text-sm text-green-800">Policies Inserted</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{uploadResults.duplicates || 0}</div>
              <div className="text-sm text-yellow-800">Duplicates Skipped</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{uploadResults.errors?.length || 0}</div>
              <div className="text-sm text-red-800">Errors</div>
            </div>
          </div>
          {uploadResults.errors && uploadResults.errors.length > 0 && (
            <div className="mt-4">
              <h5 className="text-sm font-medium text-gray-700 mb-2">Error Details:</h5>
              <div className="max-h-40 overflow-y-auto bg-gray-50 p-3 rounded text-sm">
                {uploadResults.errors.map((error, index) => (
                  <div key={index} className="mb-1 text-red-600">
                    Row {error.row}: {error.error}
                  </div>
                ))}
              </div>
            </div>
          )}
          <button
            onClick={() => setUploadResults(null)}
            className="mt-4 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      )}

      {/* Manual policy form */}
      {showManualPolicyForm && (
        <div className="bg-white p-6 rounded-lg shadow-md mt-4">
          <h4 className="text-md font-semibold text-gray-800 mb-4">Manual Policy Entry</h4>
          <p className="text-sm text-gray-600 mb-4">
            Enter policy details for advisors and leaders. Required fields are marked with *.
          </p>
          <form onSubmit={handleManualPolicySubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Date *</label>
              <input
                type="date"
                value={newPolicy.start_date}
                onChange={(e) => setNewPolicy({ ...newPolicy, start_date: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Advisor/Leader *</label>
              <select
                value={newPolicy.advisor_name}
                onChange={(e) => setNewPolicy({ ...newPolicy, advisor_name: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select advisor/leader</option>
                {(usersData || []).filter(user => user.role === 'advisor' || user.role === 'leader').map(user => (
                  <option key={user._id} value={user.name}>
                    {user.name} ({user.role})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Sum Insured *</label>
              <input
                type="number"
                value={newPolicy.sum_insured}
                onChange={(e) => setNewPolicy({ ...newPolicy, sum_insured: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                required
                min="0"
                step="0.01"
                placeholder="Enter sum insured amount"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Number of Years *</label>
              <input
                type="number"
                value={newPolicy.duration_years}
                onChange={(e) => setNewPolicy({ ...newPolicy, duration_years: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                required
                min="1"
                max="50"
                placeholder="Enter duration in years"
              />
            </div>
            <div className="md:col-span-2 flex space-x-2">
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Create Policy
              </button>
              <button
                type="button"
                onClick={() => setShowManualPolicyForm(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <Table title="Users" columns={userColumns} data={filteredUsers} />
    </div>
  );
}; // Close UsersTab component

// Poster Management Tab Component
const PosterManagementTab = ({
  posters,
  posterCategories: propPosterCategories,
  loading,
  uploading,
  selectedCategory,
  setSelectedCategory,

  viewMode,
  setViewMode,
  showUploadModal,
  setShowUploadModal,
  showEditModal,
  setShowEditModal,
  showCategoryModal,
  setShowCategoryModal,
  editingPoster,
  setEditingPoster,
  uploadForm,
  setUploadForm,
  editForm,
  setEditForm,
  categoryForm,
  setCategoryForm,
  handleUpload,
  handleEdit,
  handleDelete,
  handleCreateCategory,
  handleUpdateCategory,
  handleDeleteCategory,
  fetchPosters,
  fetchCategories
}) => {
  const [posterCategories, setPosterCategories] = useState(propPosterCategories || []);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editCategoryForm, setEditCategoryForm] = useState({ name: '', color: '' });

  const isNewPoster = (createdAt) => {
    if (!createdAt) return false;
    const now = new Date();
    const createdDate = new Date(createdAt);
    const diffInHours = (now - createdDate) / (1000 * 60 * 60);
    return diffInHours <= 168; // 7 days (7 * 24 = 168 hours)
  };

  // Fetch poster categories if not provided as prop
  useEffect(() => {
    if (!propPosterCategories || propPosterCategories.length === 0) {
      const fetchCats = async () => {
        setLoadingCategories(true);
        try {
          const categories = await postersAPI.getCategories();
          setPosterCategories(categories || []);
        } catch (error) {
          console.error('Failed to fetch poster categories:', error);
          setPosterCategories([]);
        } finally {
          setLoadingCategories(false);
        }
      };
      fetchCats();
    } else {
      setPosterCategories(propPosterCategories);
    }
  }, [propPosterCategories]);
  const posterColumns = [
    {
      header: 'Preview',
      accessor: (item) => (
        <div className="relative">
          <img
            src={`${API_BASE_URL}/posters/${item._id}/image`}
            alt={item.name}
            className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-80"
            onClick={() => window.open(`${API_BASE_URL}/posters/${item._id}/image`, '_blank')}
          />
          {isNewPoster(item.createdAt) && (
            <span className="absolute top-0 left-0 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-br rounded-tl leading-none shadow-sm">
              NEW
            </span>
          )}
        </div>
      )
    },
    { header: 'Name', accessor: 'name' },
    {
      header: 'Category', accessor: (item) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium`} style={{ backgroundColor: `${item.category?.color}20`, color: item.category?.color }}>
          {item.category?.name || 'N/A'}
        </span>
      )
    },
    { header: 'Description', accessor: (item) => item.description || 'N/A' },
    { header: 'Uploaded By', accessor: (item) => item.uploaded_by?.name || 'Unknown' },
    { header: 'Upload Date', accessor: (item) => new Date(item.createdAt).toLocaleDateString() },
    { header: 'File Size', accessor: (item) => `${(item.file_size / 1024).toFixed(1)} KB` },
    {
      header: 'Actions',
      accessor: (item) => (
        <div className="flex space-x-2">
          <button
            onClick={() => {
              setEditingPoster(item);
              setEditForm({
                name: item.name,
                description: item.description || '',
                category: item.category
              });
              setShowEditModal(true);
            }}
            className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
          >
            Edit
          </button>
          <button
            onClick={() => handleDelete(item._id)}
            className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header with controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-4">

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Categories</option>
              {posterCategories.map(category => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <GridIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-md ${viewMode === 'table' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <TableIcon className="w-5 h-5" />
            </button>
          </div>
          <span className="text-sm text-gray-600">
            Showing {posters.length} poster{posters.length !== 1 ? 's' : ''}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCategoryModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
            >
              <PlusCircleIcon className="w-5 h-5 mr-2" />
              Manage Categories
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center"
            >
              <PlusCircleIcon className="w-5 h-5 mr-2" />
              Upload Poster
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <LoadingSpinner />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {posters.map(poster => (
            <div key={poster._id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="aspect-w-3 aspect-h-4 bg-gray-200 relative">
                <img
                  src={`${API_BASE_URL}/posters/${poster._id}/image`}
                  alt={poster.name}
                  className="w-full h-48 object-cover cursor-pointer hover:opacity-80"
                  onClick={() => window.open(`${API_BASE_URL}/posters/${poster._id}/image`, '_blank')}
                />
                {isNewPoster(poster.createdAt) && (
                  <span className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded shadow-md z-10">
                    NEW
                  </span>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-1">{poster.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{poster.description || 'No description'}</p>
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium`} style={{ backgroundColor: `${poster.category?.color}20`, color: poster.category?.color }}>
                    {poster.category?.name || 'N/A'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {(poster.file_size / 1024).toFixed(1)} KB
                  </span>
                </div>
                <div className="mt-3 flex space-x-2">
                  <button
                    onClick={() => {
                      setEditingPoster(poster);
                      setEditForm({
                        name: poster.name,
                        description: poster.description || '',
                        category: poster.category
                      });
                      setShowEditModal(true);
                    }}
                    className="flex-1 px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(poster._id)}
                    className="flex-1 px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Table
          title="Poster Management"
          columns={posterColumns}
          data={posters}
        />
      )}

      {posters.length === 0 && !loading && (
        <div className="text-center py-8">
          <p className="text-gray-500">No posters found matching your criteria.</p>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Upload New Poster</h3>
              <form onSubmit={handleUpload} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Poster Image *</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Supported formats: JPEG, PNG, GIF, WebP (Max: 10MB)</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    value={uploadForm.name}
                    onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Enter poster name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category *</label>
                  <select
                    value={uploadForm.category}
                    onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="">Select a category</option>
                    {posterCategories.map(category => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows="3"
                    placeholder="Optional description"
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    disabled={uploading}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400"
                  >
                    {uploading ? 'Uploading...' : 'Upload Poster'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowUploadModal(false);
                      setUploadForm({ name: '', description: '', category: '', file: null });
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingPoster && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Poster</h3>
              <form onSubmit={handleEdit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category *</label>
                  <select
                    value={editForm.category}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  >
                    {posterCategories.map(category => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows="3"
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    Update Poster
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingPoster(null);
                      setEditForm({ name: '', description: '', category: '' });
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Manage Categories</h3>
                <button
                  onClick={() => setShowCategoryModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <div className="space-y-4">
                {/* Create Category Form */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Create New Category</h4>
                  <form onSubmit={handleCreateCategory} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Name *</label>
                      <input
                        type="text"
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Category name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Color</label>
                      <input
                        type="color"
                        value={categoryForm.color}
                        onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                        className="mt-1 block w-full h-10 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="submit"
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                      >
                        Create Category
                      </button>
                    </div>
                  </form>
                </div>

                {/* Existing Categories */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Existing Categories</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {posterCategories.map(category => (
                      <div key={category._id} className="bg-white border border-gray-200 rounded-lg p-4">
                        {editingCategoryId === category._id ? (
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-500">Name</label>
                              <input
                                type="text"
                                value={editCategoryForm.name}
                                onChange={(e) => setEditCategoryForm({ ...editCategoryForm, name: e.target.value })}
                                className="mt-1 block w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500">Color</label>
                              <input
                                type="color"
                                value={editCategoryForm.color}
                                onChange={(e) => setEditCategoryForm({ ...editCategoryForm, color: e.target.value })}
                                className="mt-1 block w-full h-8 border border-gray-300 rounded-md"
                              />
                            </div>
                            <div className="flex space-x-2 pt-1">
                              <button
                                onClick={() => {
                                  handleUpdateCategory(category._id, editCategoryForm);
                                  setEditingCategoryId(null);
                                }}
                                className="flex-1 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-xs"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingCategoryId(null)}
                                className="flex-1 px-3 py-1 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-xs"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-gray-900">{category.name}</span>
                              <div
                                className="w-4 h-4 rounded-full border border-gray-300"
                                style={{ backgroundColor: category.color }}
                              ></div>
                            </div>
                            <div className="text-sm text-gray-600 mb-3">
                              {category.count} poster{category.count !== 1 ? "s" : ""}
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => {
                                  setEditingCategoryId(category._id);
                                  setEditCategoryForm({
                                    name: category.name,
                                    color: category.color || "#6B7280"
                                  });
                                }}
                                className="flex-1 px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteCategory(category._id)}
                                className="flex-1 px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
                                disabled={category.count > 0}
                              >
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Audit Tab Component
const AuditTab = ({ data, auditLogColumns }) => (
  <Table title="Audit Logs" columns={auditLogColumns} data={data.auditLogs} />
);

// Icons are imported from ./common/Icons.jsx

// Icons are now imported from ./common/Icons.jsx

// Export all components
export {
  DashboardTab,
  PayoutsTab,
  PaidPayoutsTab,
  ReportsTab,

  UsersTab,
  PosterManagementTab,
  AuditTab
};